import { useState } from "react";
import { Upload, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import api, { formatErr } from "../lib/api";

/**
 * SuspectImportDialog — owner / admin only. Reads a PROSPECT LOG-style
 * .xlsx client-side via SheetJS, normalises the rows to our schema keys,
 * shows a preview (first 8 rows), and on confirm POSTs to
 * /api/suspects/import which dedupes by phone OR email.
 *
 * The column-name mapping is forgiving — "LODGE NAME" / "lodge name" /
 * "Name" all hit the `name` field.
 */

const COL_MAP = {
  name: ["lodge name", "name", "business", "client", "lodge"],
  star_rating: ["star rating", "stars", "rating"],
  price_per_night: ["price per night", "price/night", "rate", "price"],
  province_area: ["prov. area", "province", "area", "prov area", "province area"],
  estate_reserve: ["estate / reserve", "estate", "reserve"],
  has_wtp: ["has wtp", "wtp"],
  has_sla: ["has sla", "sla"],
  needs_system: ["needs system", "needs"],
  potential_client: ["potential client", "potential"],
  contact_name: ["contact person", "contact name", "contact"],
  contact_position: ["position", "title", "role"],
  phone: ["contact number", "phone", "cell", "mobile", "tel"],
  email: ["email address", "email"],
  first_contact_date: ["first contact", "first contact date"],
  last_contact_date: ["last contact", "last contact date"],
};

function findColumnIndex(headers, candidates) {
  const norm = headers.map((h) => String(h || "").trim().toLowerCase());
  for (const c of candidates) {
    const i = norm.indexOf(c);
    if (i >= 0) return i;
  }
  return -1;
}

export default function SuspectImportDialog({ onDone, onCancel }) {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [industry, setIndustry] = useState("lodge");
  const [filename, setFilename] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    setErr("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (!aoa.length) throw new Error("Empty spreadsheet.");
      const hdrs = (aoa[0] || []).map((h) => String(h || "").trim());
      const data = aoa.slice(1).filter((r) => Array.isArray(r) && r.some((c) => c !== "" && c != null));
      // Auto-detect column indices
      const map = {};
      Object.entries(COL_MAP).forEach(([key, candidates]) => {
        map[key] = findColumnIndex(hdrs, candidates);
      });
      setHeaders(hdrs);
      setRows(data);
      setMapping(map);
    } catch (e2) {
      setErr(e2?.message || "Could not parse file");
    }
  };

  const buildPayload = () => {
    return rows.map((r) => {
      const row = {};
      Object.entries(mapping).forEach(([key, idx]) => {
        if (idx >= 0) {
          let v = r[idx];
          if (v instanceof Date) v = v.toISOString().slice(0, 10);
          if (v != null && v !== "") row[key] = String(v).trim();
        }
      });
      return row;
    });
  };

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const payload = {
        rows: buildPayload(),
        industry,
        source: "imported_from_prospect_log",
        skip_duplicates: skipDuplicates,
      };
      const { data } = await api.post("/suspects/import", payload);
      setResult(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Import failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="suspect-import-dialog">
      {!rows.length && !result && (
        <label className="block hv-card p-6 text-center cursor-pointer hover:border-[#1FB6C1]/50 transition border-dashed">
          <FileSpreadsheet className="w-8 h-8 mx-auto text-[#1FB6C1] mb-2" />
          <div className="text-sm font-bold mb-1">Pick a PROSPECT LOG .xlsx file</div>
          <div className="text-xs text-[#a0aabf]">We'll detect columns automatically (name, contact, phone, etc.) — preview before import.</div>
          <input type="file" accept=".xlsx,.xls" onChange={onFile} className="hidden" data-testid="suspect-import-file" />
        </label>
      )}

      {err && (
        <div className="border border-rose-500/40 bg-rose-500/5 text-rose-300 text-xs p-2 rounded flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      {!!rows.length && !result && (
        <>
          <div className="text-xs text-[#a0aabf] flex items-center justify-between">
            <span><span className="text-[#1FB6C1] font-bold">{filename}</span> — {rows.length} rows detected</span>
            <button type="button" onClick={() => { setRows([]); setHeaders([]); setMapping({}); setFilename(""); }} className="text-[#647087] hover:text-white">change file</button>
          </div>

          {/* Column mapping table */}
          <div className="hv-card p-3" data-testid="suspect-import-mapping">
            <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-2">Column mapping</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.keys(COL_MAP).map((key) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <span className="w-32 text-[#a0aabf] font-mono shrink-0">{key}</span>
                  <select
                    value={mapping[key] ?? -1}
                    onChange={(e) => setMapping({ ...mapping, [key]: Number(e.target.value) })}
                    className="hv-input text-xs flex-1"
                    data-testid={`suspect-import-map-${key}`}
                  >
                    <option value={-1}>— skip —</option>
                    {headers.map((h, i) => <option key={i} value={i}>{h || `(col ${i + 1})`}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="hv-card p-3 max-h-64 overflow-auto" data-testid="suspect-import-preview">
            <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-2">Preview — first 8 rows</div>
            <table className="text-xs w-full">
              <thead><tr className="border-b border-white/10">
                <th className="text-left py-1 pr-2">Name</th>
                <th className="text-left py-1 pr-2">Phone</th>
                <th className="text-left py-1 pr-2">Email</th>
                <th className="text-left py-1 pr-2">Province</th>
                <th className="text-left py-1 pr-2">Stars</th>
              </tr></thead>
              <tbody>
                {rows.slice(0, 8).map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-1 pr-2 font-medium">{mapping.name >= 0 ? String(r[mapping.name] || "—") : "—"}</td>
                    <td className="py-1 pr-2">{mapping.phone >= 0 ? String(r[mapping.phone] || "—") : "—"}</td>
                    <td className="py-1 pr-2">{mapping.email >= 0 ? String(r[mapping.email] || "—") : "—"}</td>
                    <td className="py-1 pr-2">{mapping.province_area >= 0 ? String(r[mapping.province_area] || "—") : "—"}</td>
                    <td className="py-1 pr-2">{mapping.star_rating >= 0 ? String(r[mapping.star_rating] || "—") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-3 flex-wrap text-xs">
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={skipDuplicates} onChange={(e) => setSkipDuplicates(e.target.checked)} data-testid="suspect-import-dedupe" />
              Skip duplicates (matches phone or email)
            </label>
            <label className="flex items-center gap-1.5">
              Industry tag:
              <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="hv-input text-xs py-1" data-testid="suspect-import-industry">
                <option value="lodge">lodge</option>
                <option value="hotel">hotel</option>
                <option value="restaurant">restaurant</option>
                <option value="agriculture">agriculture</option>
                <option value="industrial">industrial</option>
                <option value="other">other</option>
              </select>
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onCancel} className="hv-btn hv-btn-ghost">Cancel</button>
            <button onClick={submit} disabled={busy || mapping.name < 0} className="hv-btn hv-btn-primary" data-testid="suspect-import-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Import {rows.length} rows
            </button>
          </div>
        </>
      )}

      {result && (
        <div className="hv-card p-4 space-y-2 border-emerald-500/30 bg-emerald-500/5" data-testid="suspect-import-result">
          <div className="flex items-center gap-2 text-emerald-300">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-bold">Import complete.</span>
          </div>
          <div className="text-sm">
            <span className="text-emerald-300 font-bold">{result.created}</span> suspects created.
            {result.skipped_duplicates > 0 && <> · <span className="text-amber-300">{result.skipped_duplicates}</span> skipped (duplicates).</>}
            {result.errors?.length > 0 && <> · <span className="text-rose-300">{result.errors.length}</span> errors.</>}
          </div>
          {result.errors?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-rose-300">Show errors</summary>
              <pre className="text-[10px] bg-black/30 p-2 mt-1 rounded overflow-auto max-h-40">{JSON.stringify(result.errors, null, 2)}</pre>
            </details>
          )}
          <div className="flex justify-end pt-1">
            <button onClick={() => onDone?.(result)} className="hv-btn hv-btn-primary text-xs" data-testid="suspect-import-done">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
